import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Search, Send } from "lucide-react";
import { Chat, Message, MessageContent } from "../../api/chatApi.ts";
import {
  differenceInDays,
  format,
  formatRelative,
  isToday,
  isYesterday,
  Locale,
  startOfDay,
  startOfToday,
} from "date-fns";
import { useLanguageStore } from "../../stores/languageStore.ts";
import { Trans, useLingui } from "@lingui/react/macro";

interface ChatListProps {
  chats: Chat[];
  currentChatId?: string;
  onChatSelect: (chat: Chat) => void;
  onBackToList?: () => void;
}

interface ChatMessagesProps {
  messages: Message[];
  currentUserId?: string;
  currentChat?: Chat;
  onSendMessage: (content: MessageContent) => void;
  onBackToList?: () => void;
}

interface CompleteChatProps {
  chats: Chat[];
  messages: Message[];
  currentChatId?: string;
  currentUserId?: string;
  onChatSelect: (chat: Chat) => void;
  onSendMessage: (content: MessageContent) => void;
  onBackToList?: () => void;
}

const getInitials = (displayName: string) => {
  return displayName
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

const formatMessageTimestamp = (createdAt: string, locale: Locale) => {
  const messageDate = new Date(createdAt);
  const daysDiff = differenceInDays(startOfToday(), startOfDay(messageDate));

  if (isToday(messageDate)) {
    return format(messageDate, "HH:mm", { locale });
  } else if (isYesterday(messageDate)) {
    return formatRelative(messageDate, new Date(), { locale });
  } else if (daysDiff >= 2) {
    return format(messageDate, "Pp", { locale });
  } else {
    return format(messageDate, "HH:mm", { locale });
  }
};

const formatDateDivider = (date: Date, locale: Locale) => {
  if (isToday(date) || isYesterday(date)) {
    return formatRelative(date, new Date(), { locale }).split(" ")[0];
  } else {
    return format(date, "PPP", { locale });
  }
};

const DateDivider: React.FC<{ date: Date }> = ({ date }) => {
  const { dateFnsLocale } = useLanguageStore();
  return (
    <div className="my-4 flex items-center justify-center">
      <div className="flex-1 border-t border-gray-300"></div>
      <div className="mx-4 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
        {formatDateDivider(date, dateFnsLocale)}
      </div>
      <div className="flex-1 border-t border-gray-300"></div>
    </div>
  );
};

// Chat List Component
const ChatList: React.FC<ChatListProps> = ({
  chats,
  currentChatId,
  onChatSelect,
}) => {
  const { t } = useLingui();
  const [searchTerm, setSearchTerm] = useState("");

  const filteredChats = chats.filter((chat) => {
    const chatName = chat.participant.name;
    return (
      chatName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chat.lastMessage?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const getChatDisplayName = (chat: Chat) => {
    return chat.participant.name || "Unknown Chat";
  };

  const getChatAvatar = (chat: Chat) => {
    if (chat.imageUrl) {
      return (
        <img
          src={chat.imageUrl}
          alt={getChatDisplayName(chat)}
          className="h-12 w-12 rounded-full object-cover"
        />
      );
    }

    const displayName = getChatDisplayName(chat);
    const initials = getInitials(displayName);

    return (
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-300 text-sm font-medium text-gray-700">
        {initials}
      </div>
    );
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return "";
    const date = new Date(timeString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  return (
    <div className="bg-soluud-background-card flex h-full w-full flex-col border-r border-gray-200 md:w-80">
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800">
            <Trans>Chats</Trans>
          </h2>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
          <input
            type="text"
            placeholder={t`Search chats...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="focus:ring-soluud-primary w-full rounded-lg border border-gray-300 py-2 pr-4 pl-10 focus:border-transparent focus:ring-2 focus:outline-none"
          />
        </div>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto">
        {filteredChats.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            {searchTerm ? t`No chats found` : t`No chats yet`}
          </div>
        ) : (
          filteredChats.map((chat) => (
            <div
              key={chat.chatId}
              onClick={() => onChatSelect(chat)}
              className={`cursor-pointer border-b border-gray-100 p-4 transition-colors hover:bg-gray-50 ${
                currentChatId === chat.chatId
                  ? "border-r-soluud-primary bg-soluud-primary-subtle border-r-2"
                  : ""
              }`}
            >
              <div className="flex items-center space-x-3">
                {getChatAvatar(chat)}

                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="truncate font-medium text-gray-900">
                      {getChatDisplayName(chat)}
                    </h3>
                    <span className="text-xs text-gray-500">
                      {formatTime(chat.lastMessageTime)}
                    </span>
                  </div>

                  <div className="mt-1 flex items-center justify-between">
                    <p className="truncate text-sm text-gray-600">
                      {chat.lastMessage || ""}
                    </p>
                    {chat.unreadCount &&
                      chat.unreadCount > 0 &&
                      currentChatId !== chat.chatId && (
                        <span className="bg-soluud-primary min-w-[20px] rounded-full px-2 py-1 text-center text-xs text-white">
                          {chat.unreadCount > 99 ? "99+" : chat.unreadCount}
                        </span>
                      )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Chat Messages Component
const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  currentUserId,
  currentChat,
  onSendMessage,
  onBackToList,
}) => {
  const { t } = useLingui();
  const [inputText, setInputText] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const newHeight = Math.min(textarea.scrollHeight, 120);
      textarea.style.height = `${newHeight}px`;
    }
  }, [inputText]);

  const handleSendMessage = () => {
    if (!inputText.trim()) return;

    const content: MessageContent = {
      text: inputText,
    };
    onSendMessage(content);
    setInputText("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getChatDisplayName = (chat?: Chat) => {
    return chat?.participant.name || "Chat";
  };

  const groupMessagesByDate = (messages: Message[]) => {
    const grouped = messages.reduce(
      (acc, message) => {
        const messageDate = new Date(message.createdAt);
        const dateKey = messageDate.toDateString();

        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(message);
        return acc;
      },
      {} as Record<string, Message[]>,
    );

    return Object.entries(grouped).map(([date, messages]) => ({
      date,
      messages,
    }));
  };
  const MessageBubble: React.FC<{ message: Message }> = ({ message }) => {
    const { dateFnsLocale } = useLanguageStore();
    const isOwn = message.userId === currentUserId;

    return (
      <div
        className={`flex ${isOwn ? "justify-end" : "justify-start"} group mb-4`}
      >
        <div
          className={`max-w-xs lg:max-w-md xl:max-w-lg ${isOwn ? "order-1" : "order-2"}`}
        >
          <div className="relative">
            <div
              className={`rounded-2xl px-4 py-2 ${
                isOwn
                  ? "bg-soluud-primary rounded-br-md text-white"
                  : "rounded-bl-md bg-gray-200 text-gray-800"
              }`}
            >
              <div className="break-words">{message.content.text}</div>

              <div className="mt-1 flex items-center justify-between text-xs opacity-70">
                <span>
                  {formatMessageTimestamp(message.createdAt, dateFnsLocale)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {!isOwn && (
          <div className="order-1 mr-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-300 text-sm font-medium">
              {getInitials(message.userName) || "U"}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!currentChat) {
    return (
      <div className="flex flex-1 items-center justify-center bg-gray-50">
        <div className="text-center text-gray-500">
          <div className="mb-2 text-2xl">💬</div>
          <p>
            <Trans>Select a chat to start messaging</Trans>
          </p>
        </div>
      </div>
    );
  }

  const groupedMessages = groupMessagesByDate(messages);

  return (
    <div className="bg-soluud-background-card flex flex-1 flex-col">
      {/* Chat Header */}
      <div className="bg-soluud-background-card border-b border-gray-200 px-6 py-4">
        <div className="flex items-center space-x-3">
          {/* Back button for mobile */}
          {onBackToList && (
            <button
              onClick={onBackToList}
              className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-gray-100 md:hidden"
              aria-label="Back to chat list"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
          )}

          {currentChat.imageUrl ? (
            <img
              src={currentChat.imageUrl}
              alt={getChatDisplayName(currentChat)}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-300 text-sm font-medium">
              {getInitials(getChatDisplayName(currentChat)).toUpperCase()}
            </div>
          )}
          <div>
            <h3 className="text-soluud-text-primary font-semibold">
              {getChatDisplayName(currentChat)}
            </h3>
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto p-4">
        {groupedMessages.map((group) => (
          <div key={group.date}>
            {/* Date divider */}
            <DateDivider date={new Date(group.date)} />

            {/* Messages for this date */}
            {group.messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t p-4">
        <div className="flex items-center space-x-2">
          <div className="flex flex-1">
            <textarea
              ref={inputRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t`Type a message...`}
              className="focus:ring-soluud-primary w-full resize-none overflow-y-auto rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:outline-none"
              rows={1}
              style={{ minHeight: "2.5rem" }}
            />
          </div>

          <div className="flex space-x-1">
            <button
              onClick={handleSendMessage}
              disabled={!inputText.trim()}
              className="bg-soluud-primary hover:bg-soluud-primary-hover rounded-lg p-2 text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Complete Chat Component
const CompleteChat: React.FC<CompleteChatProps> = ({
  chats,
  messages,
  currentChatId,
  currentUserId,
  onChatSelect,
  onSendMessage,
  onBackToList,
}) => {
  const currentChat = chats.find((chat) => chat.chatId === currentChatId);
  const currentMessages = messages.filter(
    (msg) => msg.chatId === currentChatId,
  );

  return (
    <div className="bg-soluud-background flex h-full">
      <div
        className={`${!currentChatId ? "flex" : "hidden md:flex"} ${currentChatId ? "w-0 md:w-80" : "w-full md:w-80"}`}
      >
        <ChatList
          chats={chats}
          currentChatId={currentChatId}
          onChatSelect={onChatSelect}
        />
      </div>

      <div className={`${currentChatId ? "flex" : "hidden md:flex"} flex-1`}>
        <ChatMessages
          messages={currentMessages}
          currentUserId={currentUserId}
          currentChat={currentChat}
          onSendMessage={onSendMessage}
          onBackToList={onBackToList}
        />
      </div>
    </div>
  );
};

export default CompleteChat;
